// /strategies — the storages-strategies framework surface.
//
// cortex is a storages-strategies framework: typed STORAGES connected by typed
// STRATEGYS (arrows kind: domain_type → codomain_type). A pipeline is an
// arbitrary DAG of these. This page lists the kinds, storages, and the DAG, and
// routes to each kind's own config panel (per-kind UI registry). "Add strategy"
// hands off to the ENGINE harness (only it can author a new kind in Flow).
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Shapes, Plus, ArrowRight, ExternalLink, Loader2 } from "lucide-react";
import { listStorages, listStrategies, listStrategyKinds } from "../services/pipeline";
import { getStrategyKind } from "../components/strategies";
import StrategyImplModal from "../components/strategies/StrategyImplModal";

// Open the engine harness seeded with the add-strategy prompt. AssistantChat
// listens for this CustomEvent and opens the engine chat with an initialPrompt.
const ADD_STRATEGY_PROMPT =
  "The user wants to create a new transformation. Ask them for everything you need " +
  "(a name, what it should do, and which data points/schemas it maps between — its " +
  "domain_storage_type → codomain_storage_type), then author the Flow endpoint " +
  "cortex.strategy.<kind>.apply for it and register the kind with register_strategy_kind.";

function openAddStrategy() {
  window.dispatchEvent(new CustomEvent("cortex:open-harness", {
    detail: { harness: "engine", initialPrompt: ADD_STRATEGY_PROMPT },
  }));
}

function KindPanel({ kind }) {
  const def = getStrategyKind(kind);
  if (def && def.Panel) {
    const Panel = def.Panel;
    return <Panel />;
  }
  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold mb-1">{kind}</h2>
      <p className="text-sm text-gray-500">
        No config panel is installed for this transformation kind yet. It still runs
        via its Flow endpoint; a panel can be added without redeploying the
        engine.
      </p>
    </div>
  );
}

function Overview() {
  const [kinds, setKinds] = useState([]);
  const [flowProjectId, setFlowProjectId] = useState(null);
  const [storages, setStorages] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [err, setErr] = useState(null);
  const [kindsLoading, setKindsLoading] = useState(true);
  const [dagLoading, setDagLoading] = useState(true);
  const [implModal, setImplModal] = useState(null); // { title, impl } when a card's impl is opened

  useEffect(() => {
    let cancelled = false;
    // Kinds are the first-class citizens and load fast — fetch them on their own
    // so the primary list renders immediately instead of waiting on /api/storages
    // (a heavy mapping JOIN that can take several seconds).
    (async () => {
      try {
        const k = await listStrategyKinds();
        if (cancelled) return;
        setKinds(k?.strategy_kinds || []);
        setFlowProjectId(k?.flow_project_id || null);
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setKindsLoading(false);
      }
    })();
    // The DAG section (storages + strategy instances) loads independently.
    (async () => {
      try {
        const [s, m] = await Promise.all([listStorages(), listStrategies()]);
        if (cancelled) return;
        setStorages(s?.storages || []);
        setStrategies(m?.strategies || []);
      } catch (e) {
        if (!cancelled && !err) setErr(e.message || String(e));
      } finally {
        if (!cancelled) setDagLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const storageById = useMemo(
    () => new Map(storages.map((s) => [s.storage_id, s])),
    [storages]
  );

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-[32px] font-medium leading-tight text-page-title flex items-center gap-2">
            <Shapes size={26} /> Transformations
          </h1>
          <p className="text-sm text-page-muted mt-1 max-w-2xl">
            cortex connects typed data points with typed transformations. A
            pipeline is an arbitrary DAG of these. Add a new transformation kind
            and the engine harness will author its implementation.
          </p>
        </div>
        <button onClick={openAddStrategy} data-testid="add-strategy"
          className="inline-flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors">
          <Plus size={16} /> Add transformation
        </button>
      </div>

      {err && <div className="text-sm text-[var(--error-text)]">Error: {err}</div>}

      <section>
        <h2 className="text-sm font-semibold text-page-muted uppercase tracking-wide mb-3">Transformations</h2>
        {kindsLoading && (
          <div className="flex items-center gap-2 text-sm text-page-muted py-4">
            <Loader2 size={16} className="animate-spin" /> Loading transformations…
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {kinds.map((k) => {
            // Each strategy kind is IMPLEMENTED by a Flow endpoint; deep-link to
            // it in the embedded studio. Prefer the real impl_endpoint over the
            // opaque dispatch flow_endpoint (cortex.strategy.<kind>.apply).
            const impl = k.impl_endpoint || k.flow_endpoint;
            const canOpen = flowProjectId && impl;
            return (
              <div key={k.kind} className="rounded-[8px] bg-card-bg border border-card-border px-4 py-3 hover:bg-card-bg-hover transition-colors">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-page-text">{k.display_name || k.kind}</span>
                  {k.is_static && <span className="text-[10px] uppercase bg-[var(--pill-neutral-bg)] text-[var(--pill-neutral-color)] rounded px-1.5 py-0.5">static</span>}
                </div>
                <div className="text-xs text-page-muted mt-1 flex items-center gap-1">
                  <code className="text-page-text/80">{k.domain_storage_type}</code> <ArrowRight size={12} /> <code className="text-page-text/80">{k.codomain_storage_type}</code>
                </div>
                {canOpen ? (
                  // Open the implementation in an embedded modal (iframe to the
                  // bare /flow-embed canvas) rather than navigating to the full
                  // studio — navigating churns the studio WS and races re-auth,
                  // making the canvas intermittently empty on repeat visits.
                  <button type="button" data-testid={`strategy-impl-${k.kind}`}
                    onClick={() => setImplModal({ title: `${k.display_name || k.kind} — implementation`, impl })}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-nav-active hover:text-nav-active-hover bg-transparent border-0 cursor-pointer p-0">
                    <ExternalLink size={13} /> View implementation in Flow
                    <code className="text-[11px] text-page-muted ml-1">{impl}</code>
                  </button>
                ) : (
                  <div className="mt-2 text-xs text-page-muted">No Flow implementation linked.</div>
                )}
              </div>
            );
          })}
          {!kindsLoading && kinds.length === 0 && !err && <div className="text-sm text-page-muted">No transformations registered.</div>}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-page-muted uppercase tracking-wide mb-3">
          Transformation DAG ({strategies.length} edges, {storages.length} data points)
        </h2>
        <div className="space-y-2">
          {strategies.map((m) => {
            const d = storageById.get(m.domain_storage_id);
            const c = storageById.get(m.codomain_storage_id);
            return (
              <div key={m.id} className="flex items-center gap-2 text-sm">
                <span className="rounded bg-card-bg border border-card-border text-page-text px-2 py-1">{d?.name || m.domain_storage_id}</span>
                <span className="text-page-muted flex items-center gap-1">
                  <ArrowRight size={14} /> <code className="text-xs text-nav-active">{m.kind}</code> <ArrowRight size={14} />
                </span>
                <span className="rounded bg-card-bg border border-card-border text-page-text px-2 py-1">{c?.name || m.codomain_storage_id}</span>
              </div>
            );
          })}
          {dagLoading && (
            <div className="flex items-center gap-2 text-sm text-page-muted py-2">
              <Loader2 size={16} className="animate-spin" /> Loading DAG…
            </div>
          )}
          {!dagLoading && strategies.length === 0 && !err && <div className="text-sm text-page-muted">No transformations configured.</div>}
        </div>
      </section>

      {implModal && (
        <StrategyImplModal
          title={implModal.title}
          impl={implModal.impl}
          flowProjectId={flowProjectId}
          onClose={() => setImplModal(null)}
        />
      )}
    </div>
  );
}

export default function Strategies() {
  const { kind } = useParams();
  if (kind) return <KindPanel kind={kind} />;
  return <Overview />;
}
